import { autoGeneratedReducer } from '@hh.ru/redux-create-reducer';
import { SalaryRange } from '@hh.ru/types-hh-microcore';

import CompetitionLevel from 'src/constants/competitionLevel';
import Currency from 'src/constants/currency';
import Employment from 'src/constants/employment';
import PublicationType from 'src/constants/publicationType';
import VacancyQuality from 'src/constants/vacancyQuality';
import WorkExperience from 'src/constants/workExperience';
import WorkSchedule from 'src/constants/workSchedule';

export interface SimilarVacancy {
    compensationCurrency: Currency;
    compensationFrom: number;
    compensationGross: boolean;
    compensationTo: number;
    employerName: string;
    employment: Employment;
    positionName: string;
    vacancyId: number;
}

export enum PerformanceValueName {
    ExtremelyLow = 'EXTREMELY_LOW',
    Low = 'LOW',
    Normal = 'NORMAL',
    High = 'HIGH',
    ExtremelyHigh = 'EXTREMELY_HIGH',
}

export interface Value {
    absolute: number;
    relative: number;
}

export enum AdviceType {
    PublishSalary = 'PUBLISH_SALARY',
    DecreaseWorkExperience = 'DECREASE_WORK_EXPERIENCE',
    UseStandardPlus = 'USE_STANDARD_PLUS',
    MakeSalaryCloserToMarket = 'MAKE_SALARY_CLOSER_TO_MARKET',
    SalaryMoreSpecific = 'MAKE_SALARY_MORE_SPECIFIC',
    AddConditionsToDescription = 'ADD_CONDITIONS_TO_DESCRIPTION',
    AddRequirementsToDescription = 'ADD_REQUIREMENTS_TO_DESCRIPTION',
    AddResponsibilitiesToDescription = 'ADD_RESPONSIBILITIES_TO_DESCRIPTION',
    AddWorkAddress = 'ADD_WORK_ADDRESS',
    AddEmployerContacts = 'ADD_EMPLOYER_CONTACTS',
    UseResumeSearch = 'USE_RESUME_SEARCH',
    UseSearchTop = 'USE_SEARCH_TOP',
    // tempexp_24853_next_line
    ChangeVacancyName = 'CHANGE_VACANCY_NAME',
}

export interface Performance {
    name: PerformanceValueName;
    value: number;
    points: number;
}

export enum VacancyAdviceSeverity {
    Major = 'MAJOR',
    Minor = 'MINOR',
}

export enum VacancyAdviceMetric {
    ViewCount = 'VIEW_COUNT',
    ResponseCount = 'RESPONSE_COUNT',
}

export interface VacancyAdviceDeltas {
    metric: VacancyAdviceMetric;
    before: number;
    after: number;
    deltaAbs: number;
    deltaRel: number;
}

export enum VacancyAdviceOutcome {
    ImproveVacancy = 'IMPROVE_VACANCY',
    PromoteVacancy = 'PROMOTE_VACANCY',
    IncreaseViews = 'INCREASE_VIEWS',
    AccelerateHiring = 'ACCELERATE_HIRING',
    IncreaseMetricsRel = 'INCREASE_METRICS_REL',
}

export interface PieceOfAdvice {
    advice: AdviceType;
    severity: VacancyAdviceSeverity;
    outcome: VacancyAdviceOutcome;
    deltas: VacancyAdviceDeltas[];
}

export interface DistributionItem {
    feature: string;
    value: Value;
}

export interface ParameterItem {
    parameter: string;
    value: Performance;
}

export interface SalaryItem {
    parameter: WorkExperience;
    value: SalaryStatistics;
}

export interface ApplicantsStats {
    applicantsQty: number;
    averageResponsesQty: number;
    averageVacancyPerformance: number;
    medianResponsesQty: number;
    bottomResponsesQty: number;
    upperResponsesQty: number;
    responsesQty: number;
    vacanciesWithResponses: Value;
    marketVacancyPerformance: Performance;
    suitableResumeCount: number;
}

export interface SummaryStats {
    applicantsToVacancyRatio: number;
    competitionLevel: CompetitionLevel;
    salaryRange: SalaryRange;
    vacancyQuality: VacancyQuality;
}

export interface VacancyInfo {
    vacancyId: number;
    compensationCurrency: Currency;
    compensationFrom: number | null;
    compensationGross: boolean;
    compensationTo: number | null;
    name: string;
    professionalRoleName: string;
    areaId: number;
    areaName: string;
    publicationType: PublicationType;
    workExperience: WorkExperience;
    workSchedule: WorkSchedule;
    employment: Employment;
    employerId: number;
}

export interface SalaryStatistics {
    bottom: number;
    maximal: number;
    median: number;
    minimal: number;
    upper: number;
    currency: Currency;
}

export interface VacancyResult {
    endDate: string;
    responseCount: number;
    startDate: string;
}

export enum AnalyzeResultType {
    CalculatedFull = 'CALCULATED_FULL',
    NotEnoughData = 'NOT_ENOUGH_DATA',
    NonApplicableProfRoleOther = 'NON_APPLICABLE_PROF_ROLE_OTHER',
}

export enum PerformanceComparisonWithMarket {
    LowLowNoAdvice = 'LOW_LOW_NO_ADVICE',
    LowLowHasAdvice = 'LOW_LOW_HAS_ADVICE',
    LowNormalNoAdvice = 'LOW_NORMAL_NO_ADVICE',
    LowNormalHasAdvice = 'LOW_NORMAL_HAS_ADVICE',
    NormalLowNoAdvice = 'NORMAL_LOW_NO_ADVICE',
    NormalLowHasAdvice = 'NORMAL_LOW_HAS_ADVICE',
    NormalNormalNoAdvice = 'NORMAL_NORMAL_NO_ADVICE',
    NormalNormalHasAdvice = 'NORMAL_NORMAL_HAS_ADVICE',
}

export interface SimilarVacanciesStat {
    applicantCount: number;
    averageVacancyPerformance: Performance;
    bottomResponseCount: number;
    employerQty: number;
    marketSalary: SalaryStatistics | null;
    salaryByWorkExperience: SalaryItem[];
    similarVacancies: SimilarVacancy[];
    upperResponseCount: number;
    vacancyPerformanceByPublicationType: ParameterItem[];
    vacancyPerformanceBySalaryRange: ParameterItem[];
    vacancyPerformanceByWorkExperience: ParameterItem[];
    vacancyQty: number;
    vacancyCountByEmployment: DistributionItem[];
    vacancyCountByWorkExperience: DistributionItem[];
    vacancyCountByWorkSchedule: DistributionItem[];
    vacancyCountByPublicationType: DistributionItem[];
}

export interface CompetitorAnalysisForVacancyData {
    adviceWithStatisticsList: PieceOfAdvice[];
    analyzeResultType: AnalyzeResultType;
    performance: {
        comparisonWithMarket: PerformanceComparisonWithMarket;
        predictedPerformance: Performance | null;
    };
    similarVacanciesStat: SimilarVacanciesStat;
    suitableResumeCount: number;
    summary: SummaryStats;
    vacancy: VacancyInfo;
    vacancyResults: VacancyResult;
}

export default autoGeneratedReducer<CompetitorAnalysisForVacancyData>(null);
